// /campaigns/:campaignId — detail page. Composes header, banners, tabs, and
// the 2-column layout (tab body + sidebar panels). Header is inline in the
// first PageSection so it scrolls with the content rather than being pinned.

import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Page, Typo, Flex, theme } from '../../../ui';
import PageHeader from '../../../ui/Page/PageHeader';
import PageSection from '../../../ui/Page/PageSection';
import { media } from '../../../utils/media';
import { useCampaign } from '../hooks/useCampaignsState';
import { useCampaignPermissions } from '../hooks/useCampaignPermissions';
import { selectDetailTab, setDetailTab } from '../campaignsSlice';

import CampaignHeader from '../components/detail/CampaignHeader';
import CampaignStatusStrip from '../components/detail/CampaignStatusStrip';
import CampaignTabs from '../components/detail/CampaignTabs';
import { DeletedBanner, AnonymizedBanner, UpsellOnlyBanner } from '../components/detail/Banners';
import OverviewTab from '../components/detail/OverviewTab';
import RecipientsTab from '../components/detail/RecipientsTab';
import ContentTab from '../components/detail/ContentTab';
import SettingsTab from '../components/detail/SettingsTab';
import DetailSidebar from '../components/detail/DetailSidebar';

const Breadcrumb = styled.div`
  color: ${({ theme }) => theme.colors.NEU_600};
  font-size: 13px;
  margin-bottom: 4px;
`;

const Body = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: flex-start;

  /* Stack at laptop — the 320px sidebar crowds the tab content below that. */
  ${media.lessThan('laptop')} {
    flex-direction: column;
  }
`;

const Main = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Side = styled.div`
  flex: 0 0 320px;
  width: 100%;

  ${media.lessThan('laptop')} {
    flex: 1 1 auto;
  }
`;

const CampaignDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  const permissions = useCampaignPermissions();
  const activeTab = useSelector(selectDetailTab);
  const { campaign, isLoading, isError } = useCampaign(campaignId);

  const setTab = useCallback((tab) => dispatch(setDetailTab(tab)), [dispatch]);

  if (!permissions.featureEnabled) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <Page>
        <PageSection>
          <Typo.MediumText>{t('globals.loading', 'Loading…')}</Typo.MediumText>
        </PageSection>
      </Page>
    );
  }

  if (isError || !campaign) {
    return (
      <Page>
        <PageSection>
          <Typo.MediumText>{t('campaigns.loadFailed', 'Could not load campaign.')}</Typo.MediumText>
        </PageSection>
      </Page>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'recipients':
        return <RecipientsTab campaign={campaign} />;
      case 'content':
        return <ContentTab campaign={campaign} />;
      case 'settings':
        return <SettingsTab campaign={campaign} />;
      case 'overview':
      default:
        return <OverviewTab campaign={campaign} theme={theme} />;
    }
  };

  const headerTitle = (
    <Flex direction='column' gap='4' align='flex-start'>
      <Breadcrumb>
        <button
          type='button'
          onClick={() => navigate('/campaigns')}
          style={{ background: 'none', border: 0, color: 'inherit', padding: 0, cursor: 'pointer', font: 'inherit' }}
        >
          {t('campaigns.title', 'Campaigns')}
        </button>
        {' › '}
        {campaign.name}
      </Breadcrumb>
      <Typo.SectionHeader>{campaign.name}</Typo.SectionHeader>
    </Flex>
  );

  return (
    <Page gap={20}>
      <PageSection gap={16} align='stretch'>
        <PageHeader title={headerTitle}>
          <CampaignHeader campaign={campaign} />
        </PageHeader>
        <CampaignStatusStrip campaign={campaign} />

        <DeletedBanner deletedAt={campaign.deletedAt} />
        <AnonymizedBanner anonymizedAt={campaign.anonymizedAt} />
        <UpsellOnlyBanner campaign={campaign} />

        <CampaignTabs active={activeTab} onChange={setTab} />

        <Body>
          <Main>{renderTab()}</Main>
          <Side>
            <DetailSidebar campaign={campaign} theme={theme} />
          </Side>
        </Body>
      </PageSection>
    </Page>
  );
};

export default CampaignDetailPage;
